<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CrmDealImportDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignBatch/CrmDealImportDialog.vue';
import CsvImportDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignBatch/CsvImportDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const batchId = computed(() => route.params.batchId);
const batch = useMapGetter('campaignBatches/getBatchById');
const contacts = useMapGetter('campaignBatches/getBatchContacts');
const uiFlags = useMapGetter('campaignBatches/getUIFlags');

const currentBatch = computed(() => batch.value(batchId.value));
const isFetchingContacts = computed(() => uiFlags.value.isFetchingContacts);
const isSending = computed(() => uiFlags.value.isSending);

const showCrmImport = ref(false);
const showCsvImport = ref(false);

onMounted(() => {
  store.commit('campaignBatches/SET_CAMPAIGN_BATCH_CONTACTS', []);
  
  store.dispatch('campaignBatches/show', batchId.value);
  store.dispatch('campaignBatches/fetchContacts', {
    batchId: batchId.value,
  });
  store.dispatch('crmPipelines/get');
});

const statusConfig = {
  draft: { class: 'text-n-slate-11 bg-n-alpha-2', label: t('CAMPAIGN.BATCH.STATUS.DRAFT') },
  ready: { class: 'text-n-blue-11 bg-n-blue-3', label: t('CAMPAIGN.BATCH.STATUS.READY') },
  sending: { class: 'text-n-amber-11 bg-n-amber-3', label: t('CAMPAIGN.BATCH.STATUS.SENDING') },
  completed: { class: 'text-n-teal-11 bg-n-teal-3', label: t('CAMPAIGN.BATCH.STATUS.COMPLETED') },
  failed: { class: 'text-n-ruby-11 bg-n-ruby-3', label: t('CAMPAIGN.BATCH.STATUS.FAILED') },
};

const statusBadge = computed(() => {
  if (!currentBatch.value) return statusConfig.draft;
  return statusConfig[currentBatch.value.status] || statusConfig.draft;
});

const contactStatusClass = status => {
  const map = {
    pending: 'text-n-slate-11 bg-n-alpha-2',
    sent: 'text-n-teal-11 bg-n-teal-3',
    failed: 'text-n-ruby-11 bg-n-ruby-3',
    skipped: 'text-n-amber-11 bg-n-amber-3',
  };
  return map[status] || 'text-n-slate-11 bg-n-alpha-2';
};

const contactStatusLabel = status => {
  const map = {
    pending: t('CAMPAIGN.BATCH.CONTACT_STATUS.PENDING'),
    sent: t('CAMPAIGN.BATCH.CONTACT_STATUS.SENT'),
    failed: t('CAMPAIGN.BATCH.CONTACT_STATUS.FAILED'),
    skipped: t('CAMPAIGN.BATCH.CONTACT_STATUS.SKIPPED'),
  };
  return map[status] || status;
};

const canSend = computed(() => {
  if (!currentBatch.value) return false;
  const b = currentBatch.value;
  return (
    (b.status === 'draft' || b.status === 'ready') && b.contacts_count > 0
  );
});

const canAddContacts = computed(() => {
  if (!currentBatch.value) return false;
  return (
    currentBatch.value.status === 'draft' ||
    currentBatch.value.status === 'ready'
  );
});

const stats = computed(() => {
  if (!currentBatch.value?.stats) return null;
  return [
    {
      label: 'Total',
      value: currentBatch.value.contacts_count,
      icon: 'i-lucide-users',
      color: 'text-n-slate-12',
      bg: 'bg-n-alpha-2 outline-n-container',
    },
    {
      label: t('CAMPAIGN.BATCH.CONTACT_STATUS.SENT'),
      value: currentBatch.value.stats.sent || 0,
      icon: 'i-lucide-check-check',
      color: 'text-n-teal-11',
      bg: 'bg-n-teal-2 outline-n-teal-6',
    },
    {
      label: 'Abertos',
      value: currentBatch.value.stats.opened || 0,
      icon: 'i-lucide-mail-open',
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/20 outline-blue-200 dark:outline-blue-700',
    },
    {
      label: t('CAMPAIGN.BATCH.CONTACT_STATUS.FAILED'),
      value: currentBatch.value.stats.failed || 0,
      icon: 'i-lucide-alert-circle',
      color: 'text-n-ruby-11',
      bg: 'bg-n-ruby-2 outline-n-ruby-6',
    },
    {
      label: t('CAMPAIGN.BATCH.CONTACT_STATUS.PENDING'),
      value: currentBatch.value.stats.pending || 0,
      icon: 'i-lucide-clock',
      color: 'text-n-amber-11',
      bg: 'bg-n-amber-2 outline-n-amber-6',
    },
  ];
});

const formatDate = d => {
  if (!d) return null;
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const handleRemoveContact = async contactId => {
  try {
    await store.dispatch('campaignBatches/removeContact', {
      batchId: batchId.value,
      contactId,
    });
    await store.dispatch('campaignBatches/show', batchId.value);
    useAlert(t('CAMPAIGN.BATCH.DETAIL.CONTACT_REMOVED'));
  } catch (e) {
    useAlert(t('CAMPAIGN.BATCH.DETAIL.CONTACT_REMOVE_ERROR'));
  }
};

const handleSend = async () => {
  try {
    await store.dispatch('campaignBatches/sendBatch', batchId.value);
    useAlert(t('CAMPAIGN.BATCH.DETAIL.SEND_SUCCESS'));
    await store.dispatch('campaignBatches/show', batchId.value);
  } catch (e) {
    useAlert(t('CAMPAIGN.BATCH.DETAIL.SEND_ERROR'));
  }
};

const handleImportComplete = async () => {
  showCrmImport.value = false;
  showCsvImport.value = false;
  await store.dispatch('campaignBatches/show', batchId.value);
  await store.dispatch('campaignBatches/fetchContacts', {
    batchId: batchId.value,
  });
};

const goBack = () => {
  router.push({ name: 'campaigns_batches_index' });
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-3 min-w-0">
            <Button variant="ghost" color="slate" size="sm" icon="i-lucide-arrow-left" @click="goBack" />
            <span v-if="currentBatch" class="text-heading-1 text-n-slate-12 truncate">
              {{ currentBatch.name }}
            </span>
            <span v-if="currentBatch"
              class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md flex-shrink-0"
              :class="statusBadge.class">
              {{ statusBadge.label }}
            </span>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <Button v-if="canAddContacts" variant="faded" color="slate" size="sm"
              :label="t('CAMPAIGN.BATCH.DETAIL.IMPORT_CRM')" icon="i-lucide-filter" @click="showCrmImport = true" />
            <Button v-if="canAddContacts" variant="faded" color="slate" size="sm"
              :label="t('CAMPAIGN.BATCH.DETAIL.IMPORT_CSV')" icon="i-lucide-upload" @click="showCsvImport = true" />
            <Button v-if="canSend" size="sm" :label="t('CAMPAIGN.BATCH.DETAIL.SEND')" icon="i-lucide-send"
              :is-loading="isSending" :disabled="isSending" @click="handleSend" />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <div v-if="stats" class="grid grid-cols-4 gap-3 mb-6">
          <div v-for="stat in stats" :key="stat.label"
            class="flex items-center gap-3 p-4 rounded-xl outline outline-1 -outline-offset-1" :class="stat.bg">
            <div class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-alpha-2">
              <i :class="[stat.icon, stat.color]" class="text-lg" />
            </div>
            <div class="flex flex-col">
              <span class="text-xl font-semibold" :class="stat.color">
                {{ stat.value }}
              </span>
              <span class="text-xs text-n-slate-11">{{ stat.label }}</span>
            </div>
          </div>
        </div>

        <div v-if="isFetchingContacts" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>

        <div v-else-if="!contacts.length" class="flex flex-col items-center pt-14 gap-4 text-n-slate-11">
          <div class="flex items-center justify-center w-16 h-16 rounded-2xl bg-n-alpha-2">
            <i class="i-lucide-user-x text-2xl text-n-slate-10" />
          </div>
          <p class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.BATCH.DETAIL.NO_CONTACTS') }}
          </p>
          <p class="text-sm text-n-slate-10 max-w-md text-center">
            {{ t('CAMPAIGN.BATCH.DETAIL.NO_CONTACTS_HINT') }}
          </p>
        </div>

        <div v-else
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-container overflow-hidden bg-n-solid-2">
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-n-alpha-1">
                <th class="text-left py-3 px-5 text-xs font-medium text-n-slate-10 uppercase tracking-wider">
                  {{ t('CAMPAIGN.BATCH.DETAIL.TABLE.NAME') }}
                </th>
                <th class="text-left py-3 px-5 text-xs font-medium text-n-slate-10 uppercase tracking-wider">
                  E-mail
                </th>
                <th class="text-left py-3 px-5 text-xs font-medium text-n-slate-10 uppercase tracking-wider">
                  {{ t('CAMPAIGN.BATCH.DETAIL.TABLE.STATUS') }}
                </th>
                <th class="text-left py-3 px-5 text-xs font-medium text-n-slate-10 uppercase tracking-wider">
                  Abertura
                </th>
                <th class="w-14 py-3 px-5" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="contact in contacts" :key="contact.id"
                class="border-t border-n-container hover:bg-n-alpha-1 transition-colors">
                <td class="py-3 px-5">
                  <span class="font-medium text-n-slate-12">
                    {{ contact.contact_name }}
                  </span>
                </td>
                <td class="py-3 px-5">
                  <span class="font-mono text-xs text-n-slate-11">
                    {{ contact.email || '—' }}
                  </span>
                </td>
                <td class="py-3 px-5">
                  <span class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
                    :class="contactStatusClass(contact.status)">
                    {{ contactStatusLabel(contact.status) }}
                  </span>
                  <span v-if="contact.error_message" class="text-xs text-n-ruby-10 ml-2" :title="contact.error_message">
                    {{ contact.error_message }}
                  </span>
                </td>
                <td class="py-3 px-5">
                  <div v-if="contact.opened_at" class="flex items-center gap-1.5">
                    <span class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-md">
                      <span class="i-lucide-mail-open size-3" />
                      {{ contact.open_count }}x
                    </span>
                    <span class="text-[10px] text-n-slate-9" :title="formatDate(contact.last_opened_at)">
                      {{ formatDate(contact.opened_at) }}
                    </span>
                  </div>
                  <span v-else-if="contact.status === 'sent'" class="text-xs text-n-slate-9">
                    <span class="i-lucide-mail size-3 inline-block mr-0.5" />Não aberto
                  </span>
                  <span v-else class="text-xs text-n-slate-8">—</span>
                </td>
                <td class="py-3 px-5 text-right">
                  <Button v-if="canAddContacts" variant="ghost" color="ruby" size="xs" icon="i-lucide-x"
                    @click="handleRemoveContact(contact.id)" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>

    <CrmDealImportDialog v-if="showCrmImport" :batch-id="batchId" @close="showCrmImport = false"
      @success="handleImportComplete" />

    <CsvImportDialog v-if="showCsvImport" :batch-id="batchId" @close="showCsvImport = false"
      @success="handleImportComplete" />
  </section>
</template>
